import React, {
  createContext,
  useEffect,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
  type ComponentType,
  type ReactNode,
} from 'react';

export type ObjectTreeSchema<TValue> = {
  parse: (value: unknown) => TValue;
};

export type ObjectTreeWarmScope = 'lazy' | 'page' | 'app' | 'persona';
export type RtkObjectTreeMode = 'off' | 'shadow' | 'enabled';

export type ObjectTreeResourceState<TArgs, TPayload, TView> = {
  key: string;
  args: TArgs | null;
  payload: TPayload | null;
  view: TView | null;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  scope: ObjectTreeWarmScope;
};

export type ObjectTreeResourceDefinition<
  TKey extends string,
  TArgs,
  TPayload,
  TView = TPayload,
> = {
  key: TKey;
  argsSchema: ObjectTreeSchema<TArgs>;
  payloadSchema: ObjectTreeSchema<TPayload>;
  selectView?: (payload: TPayload) => TView;
  warmScopes: readonly ObjectTreeWarmScope[];
  dependsOn?: readonly string[];
};

type AnyObjectTreeResourceDefinition = ObjectTreeResourceDefinition<
  string,
  any,
  any,
  any
>;

type ObjectTreeResourceRecord = Record<string, AnyObjectTreeResourceDefinition>;

export type RtkObjectTreeDispatch = (action: unknown) => unknown;
export type RtkObjectTreeGetState<TState = unknown> = () => TState;

export type RtkObjectTreeCacheSelector<TResult> = (state: unknown) => TResult | undefined;

export type RtkObjectTreeEndpointInitiate<TArgs> = (
  args: TArgs,
  options?: { subscribe?: boolean; forceRefetch?: boolean },
) => unknown;

export type RtkObjectTreeQueryEndpoint<TArgs, TResult> = {
  select: (args: TArgs) => RtkObjectTreeCacheSelector<TResult>;
  initiate: RtkObjectTreeEndpointInitiate<TArgs>;
  upsertQueryData?: (args: TArgs, payload: TResult) => unknown;
};

export type RtkObjectTreeMutationEndpoint<TArgs> = {
  initiate: RtkObjectTreeEndpointInitiate<TArgs>;
};

export type RtkObjectTreeResourceBinding<TArgs, TResult> = {
  endpoint: RtkObjectTreeQueryEndpoint<TArgs, TResult>;
  defaultArgs?: TArgs | (() => TArgs | null | undefined);
  seedFromBootstrap?: (
    payload: unknown,
  ) => Array<{ args: TArgs; payload: TResult }>;
};

export type RtkObjectTreeCollectionStatus =
  | 'off'
  | 'uninitialized'
  | 'loading'
  | 'fetching'
  | 'ready'
  | 'error';

export type RtkObjectTreeCollectionPageInfo = {
  nextToken: string | null;
  offset: number | null;
  limit: number | null;
  totalCount: number | null;
  hasNextPage: boolean;
};

export type RtkObjectTreeCollectionPage<TItem> = {
  items: readonly TItem[];
  nextToken?: string | null;
  offset?: number | null;
  limit?: number | null;
  totalCount?: number | null;
  hasNextPage?: boolean;
};

export type RtkObjectTreeCollectionState<TItem> = {
  collectionKey: string;
  args: unknown | null;
  items: readonly TItem[];
  nextToken: string | null;
  pageInfo: RtkObjectTreeCollectionPageInfo;
  hasNextPage: boolean;
  status: RtkObjectTreeCollectionStatus;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
};

export type RtkObjectTreeDomainGraphMappingKind =
  | 'direct-read-model'
  | 'thin-page-extractor'
  | 'domain-graph-gap';

export type RtkObjectTreeDomainGraphDescriptor = {
  boundedContext: string;
  aggregateRoot: string;
  readModel: string;
  operationId: string;
  collectionPath: string;
  pageInfoPaths: readonly string[];
  routeParamKeys: readonly string[];
  schemaSource: string;
  mappingKind: RtkObjectTreeDomainGraphMappingKind;
};

export type RtkObjectTreeCollectionBinding<TArgs, TItem, TResult> = {
  endpoint: RtkObjectTreeQueryEndpoint<TArgs, TResult>;
  initialArgs: TArgs | (() => TArgs | null | undefined);
  getNextArgs?: (
    currentArgs: TArgs,
    nextToken: string | null,
    pageInfo: RtkObjectTreeCollectionPageInfo,
  ) => TArgs | null | undefined;
  selectPage: (payload: TResult | undefined) => RtkObjectTreeCollectionPage<TItem>;
  warmScopes?: readonly ObjectTreeWarmScope[];
};

export type RtkObjectTreeSetterBinding<TArgs> = {
  endpoint: RtkObjectTreeMutationEndpoint<TArgs>;
  buildArgs: (value: unknown, context: { path: readonly string[] }) => TArgs;
};

export type RtkObjectTreeModuleDefinition = {
  id: string;
  boundedContext: string;
  aggregateRoot: string;
  domainGraph?: RtkObjectTreeDomainGraphDescriptor;
  registry: ObjectTreeRegistry<any>;
  resources?: Record<string, RtkObjectTreeResourceBinding<any, any>>;
  collections?: Record<string, RtkObjectTreeCollectionBinding<any, any, any>>;
  setters?: Record<string, RtkObjectTreeSetterBinding<any>>;
  warmScopes?: readonly ObjectTreeWarmScope[];
};

export type RtkObjectTreeMetricEvent = {
  type:
    | 'module-register'
    | 'mode-resolve'
    | 'resource-read'
    | 'resource-miss'
    | 'resource-warm'
    | 'resource-seed'
    | 'collection-read'
    | 'collection-warm'
    | 'collection-load-more'
    | 'collection-cache-hit'
    | 'collection-cache-miss'
    | 'setter-dispatch';
  moduleId: string;
  key?: string;
  mode: RtkObjectTreeMode;
  at: string;
  metadata?: Record<string, unknown>;
};

export type RtkObjectTreeInstrumentation = {
  record: (event: RtkObjectTreeMetricEvent) => void;
};

export type RtkObjectTreeFacadeOptions = {
  modules: readonly RtkObjectTreeModuleDefinition[];
  dispatch: RtkObjectTreeDispatch;
  getState: RtkObjectTreeGetState;
  mode?: RtkObjectTreeMode;
  instrumentation?: RtkObjectTreeInstrumentation;
};

export type RtkObjectTreeModuleFacade = {
  id: string;
  boundedContext: string;
  aggregateRoot: string;
  domainGraph: RtkObjectTreeDomainGraphDescriptor | null;
  mode: RtkObjectTreeMode;
  resources: Record<string, unknown>;
  collections: Record<string, RtkObjectTreeCollectionState<unknown> & {
    warm: () => unknown;
    loadMore: () => unknown;
    refresh: () => unknown;
  }>;
  warm: (scope: ObjectTreeWarmScope, resourceKeys?: readonly string[]) => unknown[];
  seedFromBootstrap: (resourceKey: string, payload: unknown) => number;
  set: (path: string, value: unknown) => unknown;
};

export type RtkObjectTreeFacade = {
  mode: RtkObjectTreeMode;
  modules: Record<string, RtkObjectTreeModuleFacade>;
  getModule: (moduleId: string) => RtkObjectTreeModuleFacade;
};

export type RtkObjectTreePreloadStatus =
  | 'unavailable'
  | 'off'
  | 'shadow'
  | 'warming'
  | 'ready'
  | 'failed';

export type RtkObjectTreePreloadOptions = {
  moduleId: string;
  scope?: ObjectTreeWarmScope;
  resourceKeys?: readonly string[];
  blockUntilReady?: boolean;
  fallback?: ReactNode;
};

export type RtkObjectTreePreloadState = {
  available: boolean;
  moduleId: string;
  mode: RtkObjectTreeMode | 'unavailable';
  status: RtkObjectTreePreloadStatus;
  scope: ObjectTreeWarmScope;
  resourceKeys: readonly string[];
  pending: boolean;
  error: unknown | null;
};

export type ObjectTreeRegistry<TResources extends ObjectTreeResourceRecord> = {
  resources: TResources;
  keys: Array<keyof TResources>;
  getResource<TKey extends keyof TResources>(key: TKey): TResources[TKey];
  getResourcesForScope(scope: ObjectTreeWarmScope): Array<TResources[keyof TResources]>;
};

export type ObjectTreeWarmPlanEntry<
  TResources extends ObjectTreeResourceRecord,
  TKey extends keyof TResources = keyof TResources,
> = {
  key: TKey;
  resource: TResources[TKey];
  scope: ObjectTreeWarmScope;
};

type ResourceArgsOf<TResource> = TResource extends ObjectTreeResourceDefinition<
  string,
  infer TArgs,
  unknown,
  unknown
>
  ? TArgs
  : never;

type ResourcePayloadOf<TResource> = TResource extends ObjectTreeResourceDefinition<
  string,
  unknown,
  infer TPayload,
  unknown
>
  ? TPayload
  : never;

type ResourceViewOf<TResource> = TResource extends ObjectTreeResourceDefinition<
  string,
  unknown,
  unknown,
  infer TView
>
  ? TView
  : never;

export type ObjectTreeModuleState<TResources extends ObjectTreeResourceRecord> = {
  resources: {
    [TKey in keyof TResources]: ObjectTreeResourceState<
      ResourceArgsOf<TResources[TKey]>,
      ResourcePayloadOf<TResources[TKey]>,
      ResourceViewOf<TResources[TKey]>
    >;
  };
};

export const defineObjectTreeResource = <
  TKey extends string,
  TArgs,
  TPayload,
  TView = TPayload,
>(
  definition: ObjectTreeResourceDefinition<TKey, TArgs, TPayload, TView>,
) => definition;

export const defineObjectTreeRegistry = <const TResources extends ObjectTreeResourceRecord>(
  resources: TResources,
): ObjectTreeRegistry<TResources> => {
  const keys = Object.keys(resources) as Array<keyof TResources>;

  return {
    resources,
    keys,
    getResource<TKey extends keyof TResources>(key: TKey) {
      return resources[key];
    },
    getResourcesForScope(scope: ObjectTreeWarmScope) {
      return keys
        .map((key) => resources[key])
        .filter((resource) => resource.warmScopes.includes(scope));
    },
  };
};

export const defineRtkObjectTreeModule = <TModule extends RtkObjectTreeModuleDefinition>(
  module: TModule,
): TModule => module;

const createMetricEvent = (
  type: RtkObjectTreeMetricEvent['type'],
  moduleId: string,
  mode: RtkObjectTreeMode,
  key?: string,
  metadata?: Record<string, unknown>,
): RtkObjectTreeMetricEvent => ({
  type,
  moduleId,
  key,
  mode,
  at: new Date().toISOString(),
  metadata,
});

const emitObjectTreeMetric = (
  instrumentation: RtkObjectTreeInstrumentation | undefined,
  event: RtkObjectTreeMetricEvent,
) => {
  instrumentation?.record(event);
};

const unwrapRtkQuerySelection = (selection: unknown): unknown => {
  if (selection != null && typeof selection === 'object' && 'data' in selection) {
    return (selection as { data?: unknown }).data;
  }
  return selection;
};

const readEndpointCache = (
  binding: RtkObjectTreeResourceBinding<unknown, unknown>,
  args: unknown,
  state: unknown,
) => unwrapRtkQuerySelection(binding.endpoint.select(args)(state));

const resolveResourceDefaultArgs = (
  binding: RtkObjectTreeResourceBinding<unknown, unknown>,
): unknown | null | undefined => (
  typeof binding.defaultArgs === 'function'
    ? binding.defaultArgs()
    : binding.defaultArgs
);

const resolveCollectionInitialArgs = (
  binding: RtkObjectTreeCollectionBinding<unknown, unknown, unknown>,
): unknown | null | undefined => (
  typeof binding.initialArgs === 'function'
    ? binding.initialArgs()
    : binding.initialArgs
);

const normalizeCollectionPageInfo = <TItem,>(
  page: RtkObjectTreeCollectionPage<TItem>,
): RtkObjectTreeCollectionPageInfo => {
  const nextToken = page.nextToken ?? null;
  const offset = page.offset ?? null;
  const limit = page.limit ?? null;
  const totalCount = page.totalCount ?? null;
  const inferredHasNextPage = nextToken != null
    || (
      offset != null
      && limit != null
      && totalCount != null
      && offset + page.items.length < totalCount
    );
  return {
    nextToken,
    offset,
    limit,
    totalCount,
    hasNextPage: page.hasNextPage ?? inferredHasNextPage,
  };
};

const selectionStatus = (selection: unknown): RtkObjectTreeCollectionStatus => {
  if (selection == null || typeof selection !== 'object') {
    return 'uninitialized';
  }
  const record = selection as {
    isError?: boolean;
    isLoading?: boolean;
    isFetching?: boolean;
    status?: string;
    data?: unknown;
    error?: unknown;
  };
  if (record.isError === true || record.status === 'rejected' || record.status === 'error') {
    return 'error';
  }
  if (record.isLoading === true || record.status === 'pending') {
    return 'loading';
  }
  if (record.isFetching === true) {
    return 'fetching';
  }
  if (record.data !== undefined || record.status === 'fulfilled' || record.status === 'success') {
    return 'ready';
  }
  return 'uninitialized';
};

const selectionErrorMessage = (selection: unknown): string | null => {
  if (selection == null || typeof selection !== 'object') {
    return null;
  }
  const error = (selection as { error?: unknown }).error;
  if (!error) {
    return null;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? '') || null;
  }
  return String(error);
};

const dispatchEndpointInitiate = (
  dispatch: RtkObjectTreeDispatch,
  endpoint: RtkObjectTreeEndpointInitiate<unknown>,
  args: unknown,
  options?: { subscribe?: boolean; forceRefetch?: boolean },
) => dispatch(endpoint(args, options));

export const seedRtkObjectTreeQueriesFromBootstrap = (
  dispatch: RtkObjectTreeDispatch,
  resource: RtkObjectTreeResourceBinding<unknown, unknown>,
  bootstrapPayload: unknown,
): number => {
  if (!resource.endpoint.upsertQueryData || !resource.seedFromBootstrap) {
    return 0;
  }

  const entries = resource.seedFromBootstrap(bootstrapPayload);
  for (const entry of entries) {
    dispatch(resource.endpoint.upsertQueryData(entry.args, entry.payload));
  }
  return entries.length;
};

const createResourceProxy = ({
  module,
  getState,
  mode,
  instrumentation,
}: {
  module: RtkObjectTreeModuleDefinition;
  getState: RtkObjectTreeGetState;
  mode: RtkObjectTreeMode;
  instrumentation?: RtkObjectTreeInstrumentation;
}) => new Proxy<Record<string, unknown>>({}, {
  get(_target, property) {
    if (typeof property !== 'string') {
      return undefined;
    }

    const binding = module.resources?.[property];
    if (!binding) {
      emitObjectTreeMetric(
        instrumentation,
        createMetricEvent('resource-miss', module.id, mode, property),
      );
      return undefined;
    }

    emitObjectTreeMetric(
      instrumentation,
      createMetricEvent('resource-read', module.id, mode, property),
    );

    if (mode === 'off') {
      return undefined;
    }

    const args = resolveResourceDefaultArgs(binding);
    if (args == null) {
      return undefined;
    }

    return readEndpointCache(binding, args, getState());
  },
});

const createCollectionFacade = ({
  module,
  key,
  binding,
  dispatch,
  getState,
  mode,
  instrumentation,
}: {
  module: RtkObjectTreeModuleDefinition;
  key: string;
  binding: RtkObjectTreeCollectionBinding<unknown, unknown, unknown>;
  dispatch: RtkObjectTreeDispatch;
  getState: RtkObjectTreeGetState;
  mode: RtkObjectTreeMode;
  instrumentation?: RtkObjectTreeInstrumentation;
}): RtkObjectTreeCollectionState<unknown> & {
  warm: () => unknown;
  loadMore: () => unknown;
  refresh: () => unknown;
} => {
  const initialArgs = resolveCollectionInitialArgs(binding);
  const selection = mode === 'off' || initialArgs == null
    ? undefined
    : binding.endpoint.select(initialArgs)(getState());
  const payload = unwrapRtkQuerySelection(selection);
  const page = binding.selectPage(payload);
  const pageInfo = normalizeCollectionPageInfo(page);
  const status = mode === 'off' ? 'off' : selectionStatus(selection);
  const cacheEventType = payload === undefined || payload === null
    ? 'collection-cache-miss'
    : 'collection-cache-hit';

  emitObjectTreeMetric(
    instrumentation,
    createMetricEvent('collection-read', module.id, mode, key, {
      itemCount: page.items.length,
      hasNextPage: pageInfo.hasNextPage,
    }),
  );
  emitObjectTreeMetric(
    instrumentation,
    createMetricEvent(cacheEventType, module.id, mode, key, {
      status,
      itemCount: page.items.length,
    }),
  );

  const initiateInitial = (forceRefetch = false) => {
    if (mode !== 'enabled' || initialArgs == null) {
      return undefined;
    }
    emitObjectTreeMetric(
      instrumentation,
      createMetricEvent('collection-warm', module.id, mode, key, {
        forceRefetch,
      }),
    );
    return dispatchEndpointInitiate(dispatch, binding.endpoint.initiate, initialArgs, {
      forceRefetch,
      subscribe: false,
    });
  };

  return {
    collectionKey: `${module.id}.${key}`,
    args: initialArgs ?? null,
    items: page.items,
    nextToken: pageInfo.nextToken,
    pageInfo,
    hasNextPage: pageInfo.hasNextPage,
    status,
    isLoading: status === 'loading',
    isFetching: status === 'fetching',
    error: selectionErrorMessage(selection),
    warm: () => initiateInitial(false),
    loadMore: () => {
      if (
        mode !== 'enabled'
        || initialArgs == null
        || !pageInfo.hasNextPage
        || !binding.getNextArgs
      ) {
        return undefined;
      }
      const nextArgs = binding.getNextArgs(initialArgs, pageInfo.nextToken, pageInfo);
      if (nextArgs == null) {
        return undefined;
      }
      emitObjectTreeMetric(
        instrumentation,
        createMetricEvent('collection-load-more', module.id, mode, key, {
          nextToken: pageInfo.nextToken,
          offset: pageInfo.offset,
          limit: pageInfo.limit,
        }),
      );
      return dispatchEndpointInitiate(dispatch, binding.endpoint.initiate, nextArgs, {
        subscribe: false,
      });
    },
    refresh: () => initiateInitial(true),
  };
};

const createCollectionsProxy = ({
  module,
  dispatch,
  getState,
  mode,
  instrumentation,
}: {
  module: RtkObjectTreeModuleDefinition;
  dispatch: RtkObjectTreeDispatch;
  getState: RtkObjectTreeGetState;
  mode: RtkObjectTreeMode;
  instrumentation?: RtkObjectTreeInstrumentation;
}) => new Proxy<Record<string, RtkObjectTreeCollectionState<unknown> & {
  warm: () => unknown;
  loadMore: () => unknown;
  refresh: () => unknown;
}>>({}, {
  get(_target, property) {
    if (typeof property !== 'string') {
      return undefined;
    }
    const binding = module.collections?.[property];
    if (!binding) {
      return undefined;
    }
    return createCollectionFacade({
      module,
      key: property,
      binding,
      dispatch,
      getState,
      mode,
      instrumentation,
    });
  },
});

const createModuleFacade = ({
  module,
  dispatch,
  getState,
  mode,
  instrumentation,
}: {
  module: RtkObjectTreeModuleDefinition;
  dispatch: RtkObjectTreeDispatch;
  getState: RtkObjectTreeGetState;
  mode: RtkObjectTreeMode;
  instrumentation?: RtkObjectTreeInstrumentation;
}): RtkObjectTreeModuleFacade => ({
  id: module.id,
  boundedContext: module.boundedContext,
  aggregateRoot: module.aggregateRoot,
  domainGraph: module.domainGraph ?? null,
  mode,
  resources: createResourceProxy({
    module,
    getState,
    mode,
    instrumentation,
  }),
  collections: createCollectionsProxy({
    module,
    dispatch,
    getState,
    mode,
    instrumentation,
  }),
  warm(scope, resourceKeys) {
    if (mode === 'off') {
      return [];
    }
    const requestedKeys = resourceKeys ? new Set(resourceKeys) : null;
    const plan = resolveObjectTreeWarmPlan(
      module.registry,
      scope,
      requestedKeys
        ? [...requestedKeys].filter((key) => key in module.registry.resources)
        : undefined,
    );
    const resourceResults = plan.flatMap((entry) => {
      const key = String(entry.key);
      const binding = module.resources?.[key];
      if (!binding) {
        return [];
      }
      const args = resolveResourceDefaultArgs(binding);
      if (args == null) {
        return [];
      }
      emitObjectTreeMetric(
        instrumentation,
        createMetricEvent('resource-warm', module.id, mode, key, { scope }),
      );
      if (mode === 'shadow') {
        return [];
      }
      return [
        dispatchEndpointInitiate(dispatch, binding.endpoint.initiate, args, {
          subscribe: false,
        }),
      ];
    });
    const collectionResults = Object.entries(module.collections ?? {}).flatMap(([key, binding]) => {
      if (requestedKeys && !requestedKeys.has(key)) {
        return [];
      }
      if (binding.warmScopes && !binding.warmScopes.includes(scope)) {
        return [];
      }
      if (!binding.warmScopes && requestedKeys == null) {
        return [];
      }
      const args = resolveCollectionInitialArgs(binding);
      if (args == null) {
        return [];
      }
      emitObjectTreeMetric(
        instrumentation,
        createMetricEvent('collection-warm', module.id, mode, key, { scope }),
      );
      if (mode === 'shadow') {
        return [];
      }
      return [
        dispatchEndpointInitiate(dispatch, binding.endpoint.initiate, args, {
          subscribe: false,
        }),
      ];
    });
    return [...resourceResults, ...collectionResults];
  },
  seedFromBootstrap(resourceKey, payload) {
    const binding = module.resources?.[resourceKey];
    if (!binding) {
      return 0;
    }
    const seeded = seedRtkObjectTreeQueriesFromBootstrap(dispatch, binding, payload);
    emitObjectTreeMetric(
      instrumentation,
      createMetricEvent('resource-seed', module.id, mode, resourceKey, { seeded }),
    );
    return seeded;
  },
  set(path, value) {
    const binding = module.setters?.[path];
    if (!binding || mode !== 'enabled') {
      return undefined;
    }
    const args = binding.buildArgs(value, { path: path.split('.') });
    emitObjectTreeMetric(
      instrumentation,
      createMetricEvent('setter-dispatch', module.id, mode, path),
    );
    return dispatchEndpointInitiate(dispatch, binding.endpoint.initiate, args);
  },
});

export const createRtkObjectTreeFacade = ({
  modules,
  dispatch,
  getState,
  mode = 'off',
  instrumentation,
}: RtkObjectTreeFacadeOptions): RtkObjectTreeFacade => {
  const moduleFacades: Record<string, RtkObjectTreeModuleFacade> = {};

  for (const module of modules) {
    emitObjectTreeMetric(
      instrumentation,
      createMetricEvent('module-register', module.id, mode),
    );
    moduleFacades[module.id] = createModuleFacade({
      module,
      dispatch,
      getState,
      mode,
      instrumentation,
    });
  }

  return {
    mode,
    modules: moduleFacades,
    getModule(moduleId) {
      const module = moduleFacades[moduleId];
      if (!module) {
        throw new Error(`RTK object-tree module is not registered: ${moduleId}`);
      }
      return module;
    },
  };
};

const RtkObjectTreeContext = createContext<RtkObjectTreeFacade | null>(null);
RtkObjectTreeContext.displayName = 'RtkObjectTreeContext';

export const RtkObjectTreeProvider = ({
  value,
  children,
}: PropsWithChildren<{ value: RtkObjectTreeFacade }>) => (
  React.createElement(RtkObjectTreeContext.Provider, { value }, children)
);

export const useRtkObjectTree = () => {
  const value = useContext(RtkObjectTreeContext);
  if (!value) {
    throw new Error('RTK object-tree facade is unavailable.');
  }
  return value;
};

export const useRtkObjectTreeModule = (moduleId: string) => (
  useRtkObjectTree().getModule(moduleId)
);

const isPromiseLike = (value: unknown): value is Promise<unknown> => (
  Boolean(value)
  && (typeof value === 'object' || typeof value === 'function')
  && typeof (value as { then?: unknown }).then === 'function'
);

const toWarmPromise = (result: unknown): Promise<unknown> | null => {
  if (!result) {
    return null;
  }
  if (
    typeof result === 'object'
    && typeof (result as { unwrap?: unknown }).unwrap === 'function'
  ) {
    return Promise.resolve((result as { unwrap: () => unknown }).unwrap());
  }
  if (isPromiseLike(result)) {
    return result;
  }
  return null;
};

export const useRtkObjectTreePreload = ({
  moduleId,
  scope = 'page',
  resourceKeys,
}: RtkObjectTreePreloadOptions): RtkObjectTreePreloadState => {
  const facade = useContext(RtkObjectTreeContext);
  const normalizedResourceKeys = useMemo(
    () => [...(resourceKeys ?? [])],
    [resourceKeys?.join('\u0000')],
  );
  const [state, setState] = useState<RtkObjectTreePreloadState>(() => ({
    available: Boolean(facade),
    moduleId,
    mode: facade?.mode ?? 'unavailable',
    status: facade ? (facade.mode === 'off' ? 'off' : 'ready') : 'unavailable',
    scope,
    resourceKeys: normalizedResourceKeys,
    pending: false,
    error: null,
  }));

  useEffect(() => {
    let cancelled = false;

    if (!facade) {
      setState({
        available: false,
        moduleId,
        mode: 'unavailable',
        status: 'unavailable',
        scope,
        resourceKeys: normalizedResourceKeys,
        pending: false,
        error: null,
      });
      return () => {
        cancelled = true;
      };
    }

    let module: RtkObjectTreeModuleFacade;
    try {
      module = facade.getModule(moduleId);
    } catch (error) {
      setState({
        available: false,
        moduleId,
        mode: facade.mode,
        status: 'unavailable',
        scope,
        resourceKeys: normalizedResourceKeys,
        pending: false,
        error,
      });
      return () => {
        cancelled = true;
      };
    }

    if (facade.mode === 'off') {
      setState({
        available: true,
        moduleId,
        mode: facade.mode,
        status: 'off',
        scope,
        resourceKeys: normalizedResourceKeys,
        pending: false,
        error: null,
      });
      return () => {
        cancelled = true;
      };
    }

    try {
      const warmResults = module.warm(scope, normalizedResourceKeys);
      const warmPromises = warmResults
        .map(toWarmPromise)
        .filter((entry): entry is Promise<unknown> => entry != null);
      if (facade.mode === 'shadow' || warmPromises.length === 0) {
        setState({
          available: true,
          moduleId,
          mode: facade.mode,
          status: facade.mode === 'shadow' ? 'shadow' : 'ready',
          scope,
          resourceKeys: normalizedResourceKeys,
          pending: false,
          error: null,
        });
        return () => {
          cancelled = true;
        };
      }

      setState({
        available: true,
        moduleId,
        mode: facade.mode,
        status: 'warming',
        scope,
        resourceKeys: normalizedResourceKeys,
        pending: true,
        error: null,
      });

      void Promise.allSettled(warmPromises).then((results) => {
        if (cancelled) {
          return;
        }
        const rejection = results.find((entry) => entry.status === 'rejected') as
          | PromiseRejectedResult
          | undefined;
        setState({
          available: true,
          moduleId,
          mode: facade.mode,
          status: rejection ? 'failed' : 'ready',
          scope,
          resourceKeys: normalizedResourceKeys,
          pending: false,
          error: rejection?.reason ?? null,
        });
      });
    } catch (error) {
      setState({
        available: true,
        moduleId,
        mode: facade.mode,
        status: 'failed',
        scope,
        resourceKeys: normalizedResourceKeys,
        pending: false,
        error,
      });
    }

    return () => {
      cancelled = true;
    };
  }, [facade, moduleId, normalizedResourceKeys, scope]);

  return state;
};

export const withRtkObjectTreePreload = <TProps extends object>(
  Component: ComponentType<TProps>,
  options: RtkObjectTreePreloadOptions,
): ComponentType<TProps> => {
  const WrappedRtkObjectTreePreload = (props: TProps) => {
    const state = useRtkObjectTreePreload(options);
    if (
      options.blockUntilReady === true
      && state.mode === 'enabled'
      && state.status === 'warming'
    ) {
      return React.createElement(React.Fragment, null, options.fallback ?? null);
    }
    return React.createElement(Component, props);
  };
  WrappedRtkObjectTreePreload.displayName = `withRtkObjectTreePreload(${
    Component.displayName ?? Component.name ?? 'Component'
  })`;
  return WrappedRtkObjectTreePreload;
};

const collectDependencyPlan = <TResources extends ObjectTreeResourceRecord>(
  registry: ObjectTreeRegistry<TResources>,
  resourceKey: keyof TResources,
  scope: ObjectTreeWarmScope,
  visited: Set<string>,
  result: Array<ObjectTreeWarmPlanEntry<TResources>>,
) => {
  const registryKey = String(resourceKey);
  if (visited.has(registryKey)) {
    return;
  }
  visited.add(registryKey);

  const resource = registry.getResource(resourceKey);
  for (const dependencyKey of resource.dependsOn ?? []) {
    if (dependencyKey in registry.resources) {
      collectDependencyPlan(
        registry,
        dependencyKey as keyof TResources,
        scope,
        visited,
        result,
      );
    }
  }

  result.push({
    key: resourceKey,
    resource,
    scope,
  });
};

export const resolveObjectTreeWarmPlan = <TResources extends ObjectTreeResourceRecord>(
  registry: ObjectTreeRegistry<TResources>,
  scope: ObjectTreeWarmScope,
  resourceKeys?: Array<keyof TResources>,
): Array<ObjectTreeWarmPlanEntry<TResources>> => {
  const keys = resourceKeys ?? registry.keys.filter((key) => (
    registry.getResource(key).warmScopes.includes(scope)
  ));
  const visited = new Set<string>();
  const result: Array<ObjectTreeWarmPlanEntry<TResources>> = [];

  for (const key of keys) {
    collectDependencyPlan(registry, key, scope, visited, result);
  }

  return result;
};

export const createObjectTreeModuleContext = <TState>(
  displayName: string,
) => {
  const ModuleContext = createContext<TState | null>(null);
  ModuleContext.displayName = `${displayName}ObjectTreeContext`;

  const Provider = ({
    value,
    children,
  }: PropsWithChildren<{ value: TState }>) => (
    React.createElement(ModuleContext.Provider, { value }, children)
  );

  const useModuleState = () => {
    const value = useContext(ModuleContext);
    if (value == null) {
      throw new Error(`${displayName} object-tree context is unavailable.`);
    }
    return value;
  };

  const createSelectorHook = <TSelected,>(
    selector: (state: TState) => TSelected,
  ) => () => selector(useModuleState());

  const withProvider = (
    value: TState,
    render: ReactNode,
  ) => React.createElement(Provider, { value }, render);

  return {
    Context: ModuleContext,
    Provider,
    useModuleState,
    createSelectorHook,
    withProvider,
  };
};
